import React, { useRef, useState } from 'react';
import { Button } from '@naf-simba/elements-buttons';
import { Typography } from '@naf-simba/building-blocks-typography';
import { Checkbox } from '@naf-simba/elements-checkbox';
import { Link } from '@naf-simba/elements-links';
import { useAuth0 } from '@auth0/auth0-react';
import { CompetitionQuestion, CompetitionStyleVariant } from '../Competition.types';
import type { CloudinaryImageType, CloudinaryVideoType } from '../../../../../types/CloudinaryImageType';
import { formatEndDate } from '../Competition.utils';
import { mapSimbaVariant } from '../../../utils/simba/mapSimbaVariant';
import CompetitionQuestionAndAnswers from '../CompetitionQuestionAndAnswers/CompetitionQuestionAndAnswers';
import * as S from './CompetitionForm.styles';
import { CompetitionHeader } from '../CompetitionHeader/CompetitionHeader';
import CompetitionTermsAndConditions from '../CompetitionTermsAndConditions/CompetitionTermsAndConditions';

interface CompetitionFormProps {
  title: string;
  ingress?: string;
  questions: CompetitionQuestion[];
  endDate?: string;
  image?: CloudinaryImageType;
  video?: CloudinaryVideoType;
  style: CompetitionStyleVariant;
  hasAnswered: boolean;
  requireTermsApproval: boolean;
  selectedAnswers: Record<string, string>;
  termsAccepted: boolean;
  isSubmitting: boolean;
  isFormValid: boolean;
  termsLinkSlug?: string;
  onAnswerSelect: (questionId: string, answerId: string) => void;
  onTermsToggle: () => void;
  onSubmit: () => void;
}

export const CompetitionForm = ({
  title,
  ingress,
  questions,
  endDate,
  image,
  video,
  style,
  hasAnswered,
  requireTermsApproval,
  selectedAnswers,
  termsAccepted,
  isSubmitting,
  isFormValid,
  termsLinkSlug,
  onAnswerSelect,
  onTermsToggle,
  onSubmit,
}: CompetitionFormProps) => {
  const { user } = useAuth0();

  const [showTerms, setShowTerms] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const formattedEndDate = formatEndDate(endDate);
  const hasTerms = Boolean(termsLinkSlug);
  const scrollToTop = () => wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleShowTerms = () => {
    setShowTerms(true);
    scrollToTop();
  };
  const handleHideTerms = () => setShowTerms(false);

  const handleSubmit = () => {
    onSubmit();
    // Prevent the page from jumping after the form is replaced by the result view.
    scrollToTop();
  };

  return (
    // The ref stays on an element that is mounted in both views, so the scroll target survives the swap.
    <S.ScrollAnchor ref={wrapperRef}>
      {showTerms && termsLinkSlug ? (
        <CompetitionTermsAndConditions style={style} onHideTerms={handleHideTerms} termsLinkSlug={termsLinkSlug} />
      ) : (
        <S.CompetitionWrapper>
          <CompetitionHeader title={title} ingress={ingress} userEmail={user?.email} hasAnswered={hasAnswered} />
          {questions.map((question) => (
            <CompetitionQuestionAndAnswers
              key={question.key}
              question={question}
              selectedAnswerKey={selectedAnswers[question.key]}
              style={style}
              image={image}
              video={video}
              onAnswerSelect={onAnswerSelect}
              hasAnswered={hasAnswered}
            />
          ))}
          {formattedEndDate && (
            <S.EndDateText>
              <Typography variant="textSRegular">Konkurransen avsluttes {formattedEndDate}</Typography>
            </S.EndDateText>
          )}
          <S.TermsContainer $style={style}>
            {!hasAnswered && requireTermsApproval && (
              <S.SpruceCheckboxWrapper $isSpruceMode={style === 'spruce'}>
                <Checkbox name="terms" label="Godkjenn vilkår" checked={termsAccepted} onChange={onTermsToggle} />
              </S.SpruceCheckboxWrapper>
            )}
            {hasTerms && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid -- Link used as a non-navigating action
              <Link onClick={handleShowTerms} text="Konkurransevilkår" inverted={style === 'spruce'} />
            )}
          </S.TermsContainer>
          {!hasAnswered && (
            <S.SubmitButtonWrapper>
              <Button
                size="medium"
                variant={mapSimbaVariant('signature').variant}
                color={mapSimbaVariant('signature').color}
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Sender...' : 'Send ditt svar'}
              </Button>
            </S.SubmitButtonWrapper>
          )}
        </S.CompetitionWrapper>
      )}
    </S.ScrollAnchor>
  );
};
